import React, { useLayoutEffect, useRef, useState } from "react";
import { Group, Image as KonvaImage, Layer, Stage } from "react-konva";
import { keepInArea } from "./placement";

/**
 * Flat view of the Smart Object's design area. The box takes the print
 * area's shape and shows the whole design (also the parts that wrap out of
 * sight on the product); drag to move it. Resizing and rotating happen on
 * the preview.
 */
export default function DesignAreaEditor({ designWidth, designHeight, image, placement, onChange, onCommit, maxHeight = 320 }) {
  const boxRef = useRef(null);
  const [boxWidth, setBoxWidth] = useState(0);

  useLayoutEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const observer = new ResizeObserver(([entry]) => setBoxWidth(entry.contentRect.width));
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  if (!designWidth || !designHeight) return <div ref={boxRef} className="ms-area" />;
  const s = boxWidth ? Math.min(boxWidth / designWidth, maxHeight / designHeight) : 0;
  const stageW = Math.max(1, Math.round(designWidth * s));
  const stageH = Math.max(1, Math.round(designHeight * s));
  const p = placement;
  // Konva reports drag positions in stage pixels.
  const keepOnArea = (pos) => {
    const kept = keepInArea({ ...p, cx: pos.x / s, cy: pos.y / s }, designWidth, designHeight);
    return { x: kept.cx * s, y: kept.cy * s };
  };

  return (
    <div ref={boxRef} className="ms-area">
      {s > 0 && (
        <Stage width={stageW} height={stageH} className="ms-area-stage">
          <Layer scaleX={s} scaleY={s}>
            <Group clipX={0} clipY={0} clipWidth={designWidth} clipHeight={designHeight}>
              {image && p && (
                <KonvaImage
                  image={image}
                  x={p.cx}
                  y={p.cy}
                  width={p.width}
                  height={p.height}
                  offsetX={p.width / 2}
                  offsetY={p.height / 2}
                  rotation={p.rotation}
                  draggable
                  dragBoundFunc={keepOnArea}
                  onDragMove={(e) => onChange?.({ ...placement, cx: e.target.x(), cy: e.target.y() })}
                  onDragEnd={(e) => onCommit?.({ ...placement, cx: e.target.x(), cy: e.target.y() }, "move")}
                />
              )}
            </Group>
          </Layer>
        </Stage>
      )}
    </div>
  );
}
